import React, { useEffect, useRef, useState } from "react";
import Logo from "src/components/molecules/logo/Logo";
import { IBusinessApprovalModel } from "src/types/models/apiModels";
import { useAuth } from "../../auth/auth-context";
import { useNavigate } from "react-router-dom";
import { BusinessListViewModel } from "./business-list-view-model";
import StatusBadge from "src/components/molecules/status-badge/status-badge";
import ButtonPro, {
	ButtonVariant,
} from "src/components/atoms/primary-button/primary-button";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";
import { CopyIcon, MailIcon } from "lucide-react";
import { ClipboardService } from "src/helpers/clipboard-service";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import LoadingPlaceHolder from "src/components/molecules/loading/loading-placeholder";
import CheckBox from "src/components/molecules/check-box/check-box";
import EmailDisplay from "src/components/atoms/email-display/email-display";
import CopyToClipboardButton from "src/components/atoms/copy-to-clipboard-button/copy-to-clipboard-button";

interface IBusinessViewerProps {
	id: string;
}

const BusinessViewer: React.FC<IBusinessViewerProps> = ({ id }) => {
	const { user } = useAuth();
	const [business, setBusiness] = useState<IBusinessApprovalModel | null>(
		null
	);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const viewModel = useRef<BusinessListViewModel | null>(null);
	const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);

	// ✅ Function to handle checkbox selection
	const handleCheckboxChange = (businessNumber: string, checked: boolean) => {
		setSelectedNumbers((prev) =>
			checked
				? [...prev, businessNumber]
				: prev.filter((num) => num !== businessNumber)
		);
	};

	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new BusinessListViewModel(user.accessToken);
			fetchBusiness();
		} else {
			// redirect to login
			navigate("/login");
		}
	}, []);

	const fetchBusiness = async () => {
		setLoading(true);
		const business = await viewModel.current?.fetchBusiness(id);
		if (business) {
			setBusiness(business);
		}
		setLoading(false);
		console.log(`business`, business);
	};

	const approveBusinessNumber = async (businessNumber: string) => {
		setLoading(true);

		const response = await viewModel.current?.approveBusinessNumber(
			id,
			businessNumber
		);

		console.log(`response`, response);

		await fetchBusiness();

		setLoading(false);
	};

	return (
		<>
			{business && (
				<div className="bg-white p-6 rounded-lg drop-shadow-2xl flex flex-col items-start justify-start gap-8 w-full pys-4">
					<div className="flex flex-col items-center justify-center gap-2 w-full">
						<div className="p-2">
							<Logo image={business.logo?.signedUrl}></Logo>
						</div>
						<span className="font-Poppins font-semibold text-lg text-nowrap">
							{business.name}
						</span>
						{business.status && (
							<StatusBadge status={business.status}></StatusBadge>
						)}
						{/* </div> */}
					</div>

					<BoxWithIcon icon={undefined} title={"Primary contact"}>
						<div className="flex items-center justify-start gap-2">
							<span className="font-Poppins font-semibold text-lg text-nowrap">
								{business.primaryContact?.firstName}{" "}
								{business.primaryContact?.lastName}
							</span>
							|
							<MailIcon className="w-4 h-4 text-gray-900"></MailIcon>
							<EmailDisplay
								emailAddress={business.primaryContact?.emailId}
							></EmailDisplay>
							<CopyToClipboardButton
								text={business.primaryContact?.emailId}
							></CopyToClipboardButton>
							{business.primaryContact &&
								business.primaryContact.phoneNumber && (
									<>
										|
										<PhoneNumberDisplay
											number={`${business.primaryContact.phoneNumber}`}
											hasPlus={true}
										></PhoneNumberDisplay>
										<button
											className="pr-2 cursor-copy"
											onClick={(e) => {
												e.stopPropagation();
												ClipboardService.copyToClipboard(
													business.primaryContact
														.phoneNumber
												);
											}}
										>
											<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
										</button>
									</>
								)}
						</div>
					</BoxWithIcon>

					{business.approvalDetails && (
						<BoxWithIcon
							icon={undefined}
							title={"Approval details"}
						>
							<div className="w-full overflow-x-auto">
								<table className="w-full border-collapse border border-slate-200">
									<tbody>
										{/* Approved At */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												Approved at
											</td>
											<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
												{new Date(
													business.approvalDetails
														.approvedAt * 1000
												).toLocaleString(undefined, {
													dateStyle: "medium",
													timeStyle: "short",
												})}
											</td>
										</tr>

										{/* Support Email */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												Support email
											</td>
											<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
												<div className="flex items-center justify-start gap-2">
													<EmailDisplay
														emailAddress={
															business
																.approvalDetails
																.supportEmailAddress
														}
													></EmailDisplay>
													<button
														className="pr-2 cursor-copy"
														onClick={(e) => {
															e.stopPropagation();
															ClipboardService.copyToClipboard(
																business
																	.primaryContact
																	?.emailId
															);
														}}
													>
														<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
													</button>
												</div>
											</td>
										</tr>

										{/* Approved By */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												Approved by
											</td>
											<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
												<div className="flex items-center justify-start gap-2">
													<EmailDisplay
														emailAddress={
															business
																.approvalDetails
																.approvedBy
																.emailId
														}
													></EmailDisplay>

													<button
														className="pr-2 cursor-copy"
														onClick={(e) => {
															e.stopPropagation();
															ClipboardService.copyToClipboard(
																business
																	.primaryContact
																	?.emailId
															);
														}}
													>
														<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
													</button>
												</div>
											</td>
										</tr>

										{/* Application ID */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												Application id
											</td>
											<td className="border border-slate-200 p-2 font-medium w-3/4">
												{business.approvalDetails
													.cPaaSProviderConfig
													?.applicationId ? (
													<div className="flex items-center justify-start gap-2">
														<span className="font-Poppins font-medium relative">
															{
																business
																	.approvalDetails
																	.cPaaSProviderConfig
																	.applicationId
															}
														</span>
														<button
															className="pr-2 cursor-copy"
															onClick={(e) => {
																e.stopPropagation();
																ClipboardService.copyToClipboard(
																	business
																		.approvalDetails
																		.cPaaSProviderConfig
																		.applicationId
																);
															}}
														>
															<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
														</button>
													</div>
												) : (
													<span className="text-gray-500">
														No secret available
													</span>
												)}
											</td>
										</tr>

										{/* API Signature Secret (Masked) */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												API signature secret
											</td>
											<td className="border border-slate-200 p-2 font-medium w-3/4">
												{business.approvalDetails
													.cPaaSProviderConfig
													?.apiSignatureSecret ? (
													<span className="relative">
														<span>
															{"•".repeat(
																business
																	.approvalDetails
																	.cPaaSProviderConfig
																	.apiSignatureSecret
																	.length - 4
															)}
														</span>
														<span>
															{business.approvalDetails.cPaaSProviderConfig.apiSignatureSecret.slice(
																-4
															)}
														</span>
													</span>
												) : (
													<span className="text-gray-500">
														No secret available
													</span>
												)}
											</td>
										</tr>

										{/* API Signature Secret (Masked) */}
										<tr className="hover:bg-slate-100">
											<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
												Approver&apos;s comments
											</td>
											<td className="border border-slate-200 p-2 font-medium w-3/4">
												{
													business.approvalDetails
														.comments
												}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</BoxWithIcon>
					)}

					<BoxWithIcon icon={undefined} title={"Business details"}>
						<div className="w-full overflow-x-auto">
							<table className="w-full border-collapse border border-slate-200">
								<tbody>
									{/* DUNS Number */}
									<tr className="hover:bg-slate-100">
										<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
											DUNS number
										</td>
										<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
											{business.dunsNumber}
										</td>
									</tr>

									{/* Country Code */}
									<tr className="hover:bg-slate-100">
										<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
											Country code
										</td>
										<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
											{business.countryCode}
										</td>
									</tr>

									{/* Industry */}
									<tr className="hover:bg-slate-100">
										<td className="border border-slate-200 p-2 font-semibold text-gray-800 w-1/4">
											Industry
										</td>
										<td className="font-Poppins font-medium border border-slate-200 p-2 w-3/4">
											{business.industry}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</BoxWithIcon>

					{business?.attributeSections && (
						<BoxWithIcon
							icon={undefined}
							title={"Additional information"}
						>
							{business?.attributeSections?.map(
								(attributeSection) => (
									<div
										className="flex flex-col items-center justify-between gap-4 p-2 mb-1"
										key={attributeSection.sectionId}
									>
										<div className="w-full overflow-x-auto">
											<table className="w-full border-collapse border border-slate-200">
												<thead>
													{/* <tr className="bg-slate-300">
														<th className="border border-slate-200 p-2 text-left w-1/4">
															Name
														</th>
														<th className="border border-slate-200 p-2 text-left w-3/4">
															Value
														</th>
													</tr> */}
												</thead>
												<tbody>
													{attributeSection.attributes?.map(
														(attribute) => (
															<tr
																key={
																	attribute
																		.value
																		.name
																}
																className="hover:bg-slate-100"
															>
																<td className="font-Poppins font-medium border border-slate-200 p-2 text-gray-800 w-1/4 first-word-capitalize lowercase">
																	{
																		attribute.title
																	}
																</td>
																<td className="font-Poppins font-medium border border-slate-200 p-2 text-gray-600 w-3/4">
																	{
																		attribute
																			.value
																			.value
																	}
																</td>
															</tr>
														)
													)}
												</tbody>
											</table>
										</div>
									</div>
								)
							)}
						</BoxWithIcon>
					)}

					{/* numbers */}
					<BoxWithIcon icon={undefined} title={"Business numbers"}>
						<div className="flex flex-col items-start justify-start gap-4 w-full">
							<div className="w-full flex flex-col items-center justify-center gap-1">
								{business?.businessNumbers?.map((number) => (
									<div
										className="bg-slate-100 flex items-center justify-between gap-2 p-2 border border-slate-200 hover:bg-slate-200 w-full"
										key={number.businessNumber}
									>
										<div className="flex items-center justify-center gap-4 px-2">
											<CheckBox
												checked={
													selectedNumbers.includes(
														number.businessNumber
													) ||
													number.status === "Active"
												}
												onChange={(checked) =>
													handleCheckboxChange(
														number.businessNumber,
														checked
													)
												}
												disabled={
													number.status === "Active"
												}
											/>
											<span className="font-Poppins font-semibold text-lg w-full text-nowrap">
												<PhoneNumberDisplay
													number={
														number.businessNumber
													}
													hasPlus={false}
												/>
											</span>
											<StatusBadge
												status={number.status}
											/>
										</div>
										<div>
											<ButtonPro
												label={"Approve"}
												disabled={
													number.status ===
														"Active" ||
													!selectedNumbers.includes(
														number.businessNumber
													)
												}
												onClick={() => {
													approveBusinessNumber(
														number.businessNumber
													);
												}}
											/>
										</div>
									</div>
								))}
							</div>
							<div>
								<ButtonPro
									label={"Add more numbers"}
									disabled
								/>
							</div>
						</div>
					</BoxWithIcon>

					<BoxWithIcon
						icon={undefined}
						title={"CPaaS Provider Config"}
					>
						<div className="flex flex-col items-start justify-start gap-4 w-full">
							<textarea
								className="font-Poppins tracking-wide w-full p-2 mb-2 text-md text-gray-900 border border-gray-600 resize-y min-h-[20px]"
								rows={4}
								value={""}
								onChange={(e) => console.log(e)}
								placeholder="Comments"
							/>
							<InputBox
								label={"Api Key"}
								type={"text"}
								onChange={function (value: string): void {
									throw new Error(
										"Function not implemented."
									);
								}}
							></InputBox>
							<InputBox
								label={"Application Id"}
								type={"text"}
								onChange={function (value: string): void {
									throw new Error(
										"Function not implemented."
									);
								}}
							></InputBox>
							<InputBox
								label={"Signature secret"}
								type={"password"}
								onChange={function (value: string): void {
									throw new Error(
										"Function not implemented."
									);
								}}
							></InputBox>
						</div>
					</BoxWithIcon>

					<div className="flex items-center justify-end gap-4 px-2 w-full">
						<div>
							<ButtonPro
								label={"Approve"}
								disabled={
									business.status === "Active" ||
									business.status === "Approved"
								}
							></ButtonPro>
						</div>
						<div>
							<ButtonPro
								variant={ButtonVariant.Secondary}
								label={"Reject"}
								disabled={
									business.status === "Active" ||
									business.status === "Approved"
								}
							></ButtonPro>
						</div>
					</div>
				</div>
			)}
			{loading && (
				<LoadingPlaceHolder animate={loading}></LoadingPlaceHolder>
			)}
		</>
	);
};

export default BusinessViewer;
